<template>
  <div
    :class="sticky ? 'breadcrumb-sticky' : 'breadcrumb-plain'"
    :style="sticky && stickyTop !== null ? { top: `${stickyTop}px` } : null"
  >
    <b-breadcrumb class="breadcrumb mb-0 pb-0 pt-1 noprint">
      <!-- Iterar sobre todos los items -->
      <b-breadcrumb-item
        v-for="(item, index) in items"
        :key="index"
        :to="isLastItem(index) ? undefined : item.to"
        :active="isLastItem(index)"
      >
        <!-- Si tiene dropdownOptions, mostrar dropdown -->
        <b-dropdown
          v-if="getDropdownOptions(item).length > 0"
          variant="link"
          class="breadcrumb-dropdown"
        >
          <template #button-content>
            <span class="dropdown-text">{{ item.text }}</span>
          </template>
          <b-dropdown-item
            v-for="(option, idx) in getDropdownOptions(item)"
            :key="idx"
            :to="option.to"
            @click="option.click && option.click()"
            class="ml-0"
          >
            <img
              v-if="option.icon"
              :src="getIconSrc(option.icon)"
              :alt="option.icon"
              class="dropdown-icon-img mr-2"
            />
            <span class="dropdown-icon mr-2" v-else>
              <b-icon icon="caret-right-fill"></b-icon>
            </span>
            <span>{{ option.text }}</span>
          </b-dropdown-item>
        </b-dropdown>

        <!-- Si no tiene dropdown, mostrar texto normal -->
        <span v-else>{{ item.text }}</span>
      </b-breadcrumb-item>
    </b-breadcrumb>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import * as names from "@/store/names";

export default {
  name: "NavigationBreadcrumb",
  props: {
    items: {
      type: Array,
      required: true,
    },
    // Fija el breadcrumb bajo el navbar al hacer scroll. Las vistas con barras
    // flotantes propias (p. ej. NewEvaluationView) pueden pasar :sticky="false"
    // para dejarlo en flujo normal y evitar que se solape.
    sticky: {
      type: Boolean,
      default: true,
    },
    egressProfileId: {
      type: Number,
      default: null,
    },
    matterId: {
      type: Number,
      default: null,
    },
    sectionId: {
      type: Number,
      default: null,
    },
    showEgressProfile: {
      type: Boolean,
      default: false,
    },
    showMatrix1: {
      type: Boolean,
      default: false,
    },
    showMatrix2: {
      type: Boolean,
      default: false,
    },
    showMatrix3: {
      type: Boolean,
      default: false,
    },
    showMatrix4: {
      type: Boolean,
      default: false,
    },
    showStudyPlan: {
      type: Boolean,
      default: false,
    },
    showCurricularMesh: {
      type: Boolean,
      default: false,
    },
    // Carga Académica (solo $usesAcademicLoad): también aparece donde se ofrece el Plan de
    // Estudio o la Malla, porque está entre ambos en el flujo del perfil.
    showAcademicLoad: {
      type: Boolean,
      default: false,
    },
    showMatterProgram: {
      type: Boolean,
      default: false,
    },
    showPlannification: {
      type: Boolean,
      default: false,
    },
    // Opciones de evaluación (profesor)
    showAssessmentStudents: {
      type: Boolean,
      default: false,
    },
    showEvaluatorEvaluations: {
      type: Boolean,
      default: false,
    },
    showAssessmentRangeSectionView: {
      type: Boolean,
      default: false,
    },
    showProfessorProgressionReportView: {
      type: Boolean,
      default: false,
    },
    // Opciones de evaluación (estudiante)
    showAssessmentStudent: {
      type: Boolean,
      default: false,
    },
    showEvaluateeEvaluations: {
      type: Boolean,
      default: false,
    },
    showAchievements: {
      type: Boolean,
      default: false,
    },
    showNewFullGradingView: {
      type: Boolean,
      default: false,
    },
    // Learning Suitcase (Formatos de salida)
    showMatterDescriptionView: {
      type: Boolean,
      default: false,
    },
    showLearningEvaluationView: {
      type: Boolean,
      default: false,
    },
    showLearningExperiencesView: {
      type: Boolean,
      default: false,
    },
    showPiaView: {
      type: Boolean,
      default: false,
    },
    showPaView: {
      type: Boolean,
      default: false,
    },
    showPdaView: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      egressProfileMatterId: null,
      matterIdFromSection: null,
      fetchingMatter: false,
      // Offset del sticky medido desde el navbar real; null = usar el CSS de
      // respaldo. En responsive el navbar tiene height:auto (filas variables),
      // por lo que un top fijo no calza en todos los anchos.
      stickyTop: null,
    };
  },
  mounted() {
    if (!this.sticky) return;
    this.updateStickyTop();
    window.addEventListener("resize", this.updateStickyTop);
    const navbar = document.getElementById("nav-bar");
    if (navbar && typeof ResizeObserver !== "undefined") {
      this.navbarResizeObserver = new ResizeObserver(this.updateStickyTop);
      this.navbarResizeObserver.observe(navbar);
    }
    // Marca en body que la vista tiene breadcrumb: el navbar cede su
    // box-shadow al breadcrumb (ver .navbar en NavBar.vue). Mismo patrón que
    // body.kl-rail-expanded del riel.
    document.body.classList.add("kl-has-breadcrumb");
  },
  beforeDestroy() {
    if (!this.sticky) return;
    window.removeEventListener("resize", this.updateStickyTop);
    if (this.navbarResizeObserver) this.navbarResizeObserver.disconnect();
    document.body.classList.remove("kl-has-breadcrumb");
  },
  computed: {
    ...mapGetters({
      user: "getUser",
      institution: "getInstitution",
      sections: names.SECTIONS,
      egress_profile_matters: names.EGRESS_PROFILES_MATTERS,
    }),
    section() {
      if (!this.sectionId) return null;
      return this.sections.find((x) => x.id == this.sectionId);
    },
    egressProfileMatter() {
      if (this.section && this.section.egress_profile_matter != null)
        return this.egress_profile_matters.find(
          (x) => x.id == this.section.egress_profile_matter
        );
      else return null;
    },
  },
  methods: {
    updateStickyTop() {
      const navbar = document.getElementById("nav-bar");
      if (!navbar) return;
      // 2px de solape con el borde inferior del navbar para que no quede una
      // rendija donde se vea pasar el contenido (mismo criterio que el CSS).
      this.stickyTop = Math.max(
        0,
        Math.round(navbar.getBoundingClientRect().bottom) - 2
      );
    },
    redirectToMatterProgram() {
      const egress_profile_matter = this.egressProfileMatter;
      const matter_id = egress_profile_matter
        ? egress_profile_matter.id
        : this.matterId;
      // Perfil de egreso real de la asignatura (misma fuente que cargará
      // MatterProgramView); se usa egressProfileId solo como respaldo.
      const matter_egress_profile = egress_profile_matter
        ? egress_profile_matter.egress_profile
        : this.egressProfileId;
      // Si el estudiante no pertenece al perfil de egreso de la asignatura,
      // se le redirige a la malla curricular de dicho perfil.
      if (
        this.user &&
        this.user.student_card &&
        this.user.group_names.includes("ESTUDIANTES") &&
        matter_egress_profile &&
        this.user.student_card.egress_profile != matter_egress_profile
      ) {
        this.$router.push({
          name: "CurricularMeshView",
          params: {
            egress_profile_id: matter_egress_profile,
          },
        });
        return;
      }
      this.$router.push({
        name: "MatterProgramView",
        params: {
          matter_id: matter_id,
        },
      });
    },
    getIconSrc(iconName) {
      try {
        return require(`@/assets/icons-breadcrumb/${iconName}.svg`);
      } catch (e) {
        return null;
      }
    },
    isLastItem(index) {
      return index == this.items.length - 1;
    },
    getDropdownOptions(item) {
      // Si el item no existe o no tiene dropdownOptions, retornar array vacío
      if (
        !item ||
        !item.dropdownOptions ||
        !Array.isArray(item.dropdownOptions)
      ) {
        return [];
      }

      // Generar opciones dinámicamente según los props show* especificados en dropdownOptions
      const options = this.getOptionsBreadcrumb(item.dropdownOptions);

      return options;
    },
    getOptionsBreadcrumb(enabledOptions = []) {
      const options = [];

      // Si enabledOptions está vacío, usar todas las opciones (comportamiento legacy)
      const shouldInclude = (optionName) => {
        return (
          enabledOptions.length == 0 || enabledOptions.includes(optionName)
        );
      };

      // Perfil de Egreso
      if (
        shouldInclude("showEgressProfile") &&
        this.showEgressProfile &&
        (this.egressProfileId || this.egressProfileMatterId)
      ) {
        options.push({
          text: this.$getVisibleNames(
            "mesh.egressprofile",
            false,
            "Perfil de Egreso"
          ),
          icon: "school",
          to: {
            name: "EgressProfileView",
            params: {
              egress_profile_id:
                this.egressProfileId || this.egressProfileMatterId,
            },
          },
        });
      }
      // Matriz 1
      if (
        shouldInclude("showMatrix1") &&
        this.showMatrix1 &&
        (this.egressProfileId || this.egressProfileMatterId)
      ) {
        options.push({
          text: this.$getVisibleNames("manual.matriz_1", false, "Matriz 1"),
          icon: "view_list",
          to: {
            name: "Matrix1View",
            params: {
              egress_profile_id:
                this.egressProfileId || this.egressProfileMatterId,
            },
          },
        });
      }
      // Matriz 2
      if (
        shouldInclude("showMatrix2") &&
        this.showMatrix2 &&
        (this.egressProfileId || this.egressProfileMatterId)
      ) {
        options.push({
          text: this.$getVisibleNames("manual.matriz_2", false, "Matriz 2"),
          icon: "view_module",
          to: {
            name: "Matrix2View",
            params: {
              egress_profile_id:
                this.egressProfileId || this.egressProfileMatterId,
            },
          },
        });
      }

      // Matriz 3
      if (
        shouldInclude("showMatrix3") &&
        this.showMatrix3 &&
        (this.egressProfileId || this.egressProfileMatterId)
      ) {
        options.push({
          text: this.$getVisibleNames("manual.matriz_3", false, "Matriz 3"),
          icon: "view_quilt",
          to: {
            name: "Matrix3View",
            params: {
              egress_profile_id:
                this.egressProfileId || this.egressProfileMatterId,
            },
          },
        });
      }

      // Matriz 4 (solo para Duoc)
      if (
        shouldInclude("showMatrix4") &&
        this.showMatrix4 &&
        this.institution &&
        this.institution.internal_use_id == "duoc.cl" &&
        (this.egressProfileId || this.egressProfileMatterId)
      ) {
        options.push({
          text: this.$getVisibleNames("manual.matriz_4", false, "Matriz 4"),
          icon: "view_compact",
          to: {
            name: "Matrix4View",
            params: {
              egress_profile_id:
                this.egressProfileId || this.egressProfileMatterId,
            },
          },
        });
      }

      // Plan de Estudios
      if (
        shouldInclude("showStudyPlan") &&
        this.showStudyPlan &&
        (this.egressProfileId || this.egressProfileMatterId)
      ) {
        options.push({
          text: this.$t("reusable.customFab.studyPlan"),
          icon: "public",
          to: {
            name: "StudyPlanView",
            params: {
              egress_profile_id:
                this.egressProfileId || this.egressProfileMatterId,
            },
          },
        });
      }

      // Carga Académica
      if (
        this.$usesAcademicLoad(this.institution) &&
        ["showAcademicLoad", "showStudyPlan", "showCurricularMesh"].some(
          (option) => shouldInclude(option) && this[option]
        ) &&
        this.$route.name != "AcademicLoadView" &&
        (this.egressProfileId || this.egressProfileMatterId)
      ) {
        options.push({
          text: this.$getVisibleNames(
            "manual.academic_load",
            false,
            this.$t("reusable.customFab.academicLoad")
          ),
          icon: "table_chart",
          to: {
            name: "AcademicLoadView",
            params: {
              egress_profile_id:
                this.egressProfileId || this.egressProfileMatterId,
            },
          },
        });
      }

      // Malla Curricular
      if (
        shouldInclude("showCurricularMesh") &&
        this.showCurricularMesh &&
        (this.egressProfileId || this.egressProfileMatterId)
      ) {
        options.push({
          text: this.$t("reusable.customFab.curricularMesh"),
          icon: "dashboard",
          to: {
            name: "CurricularMeshView",
            params: {
              egress_profile_id:
                this.egressProfileId || this.egressProfileMatterId,
            },
          },
        });
      }

      // Programa de Asignatura
      // Para instituciones NO Duoc - Mostrar "Programa de Asignatura"
      if (
        shouldInclude("showMatterProgram") &&
        this.showMatterProgram &&
        (this.egressProfileMatter || this.matterId)
      ) {
        options.push({
          text: this.$getVisibleNames(
            "manual.matter_program",
            false,
            "Programa de Asignatura"
          ),
          icon: "library_books",
          click: () => this.redirectToMatterProgram(),
        });
      }

      // FORMATOS DE SALIDA
      // Descripción Asignatura
      if (
        shouldInclude("showMatterDescriptionView") &&
        this.showMatterDescriptionView &&
        this.institution &&
        this.institution.internal_use_id == "duoc.cl" &&
        (this.egressProfileMatter || this.matterId)
      ) {
        options.push({
          text: this.$t("reusable.customFab.matterDescription"),
          icon: "text_ad",
          to: {
            name: "LearningMatterDescription",
            params: {
              egress_profile_matter_id:
                this.egressProfileMatter?.id || this.matterId,
            },
          },
        });
      }

      // Evaluación
      if (
        shouldInclude("showLearningEvaluationView") &&
        this.showLearningEvaluationView &&
        (this.egressProfileMatter || this.matterId)
      ) {
        options.push({
          text: this.$t("reusable.customFab.evaluation"),
          icon: "grid_on",
          to: {
            name: "LearningEvaluationView",
            params: {
              egress_profile_matter_id:
                this.egressProfileMatter?.id || this.matterId,
            },
          },
        });
      }

      // Experiencias de Aprendizaje
      if (
        shouldInclude("showLearningExperiencesView") &&
        this.showLearningExperiencesView &&
        this.institution &&
        this.institution.internal_use_id == "duoc.cl" &&
        (this.egressProfileMatter || this.matterId)
      ) {
        options.push({
          text: this.$t("reusable.customFab.learningExperiences"),
          icon: "table_rows",
          to: {
            name: "LearningExperiencesView",
            params: {
              egress_profile_matter_id:
                this.egressProfileMatter?.id || this.matterId,
            },
          },
        });
      }
      // Learning Suitcase (PIA)
      if (
        shouldInclude("showPiaView") &&
        this.institution &&
        this.institution.internal_use_id == "duoc.cl" &&
        this.showPiaView &&
        (this.egressProfileMatter || this.matterId)
      ) {
        options.push({
          text: this.$t("reusable.customFab.pia"),
          icon: "browse",
          to: {
            name: "LearningSuitcaseView",
            params: {
              egress_profile_matter_id:
                this.egressProfileMatter?.id || this.matterId,
            },
          },
        });
      }
      // Para Duoc - PDA (Profesor)
      if (
        shouldInclude("showPdaView") &&
        this.showPdaView &&
        this.institution &&
        this.institution.internal_use_id == "duoc.cl" &&
        (this.egressProfileMatter || this.matterId)
      ) {
        options.push({
          text: this.$t("reusable.customFab.pdaTeacher"),
          icon: "widget_width",
          to: {
            name: "LearningSuitcaseTeacherView",
            params: {
              egress_profile_matter_id:
                this.egressProfileMatter?.id || this.matterId,
            },
          },
        });
      }
      // Para Duoc - PA (Estudiante)
      if (
        shouldInclude("showPaView") &&
        this.showPaView &&
        this.institution &&
        this.institution.internal_use_id == "duoc.cl" &&
        (this.egressProfileMatter || this.matterId)
      ) {
        options.push({
          text: this.$t("reusable.customFab.paStudent"),
          icon: "team_dashboard",
          to: {
            name: "LearningSuitcaseStudentView",
            params: {
              egress_profile_matter_id:
                this.egressProfileMatter?.id || this.matterId,
            },
          },
        });
      }

      // Evaluación de la Sección (solo IPSS)
      if (
        shouldInclude("showAssessmentStudent") &&
        this.showAssessmentStudent &&
        this.institution &&
        this.institution.uses_ipss_assessment == true &&
        this.sectionId
      ) {
        options.push({
          text: `${this.$t(
            "reusable.customFab.assessmentOf"
          )} ${this.$getVisibleNames("teaching.section", false, "Sección")}`,
          icon: "assignment",
          to: {
            name: "AssessmentStudentView",
            params: { section_id: this.sectionId },
          },
        });
      }

      // Evaluaciones de la Sección (Estudiante)
      if (
        shouldInclude("showEvaluateeEvaluations") &&
        this.showEvaluateeEvaluations &&
        this.sectionId
      ) {
        options.push({
          text: `${this.$getVisibleNames(
            "evaluations2.evaluation",
            true,
            "Evaluaciones"
          )} ${this.$t("reusable.customFab.of")} ${this.$getVisibleNames(
            "teaching.section",
            false,
            "Sección"
          )}`,
          icon: "lists",
          to: {
            name: "EvaluateeEvaluationsListView",
            params: { section_id: this.sectionId },
          },
        });
      }

      // Vistas de profesor
      if (this.sectionId) {
        // Evaluación por Estudiante
        if (
          shouldInclude("showAssessmentStudents") &&
          this.showAssessmentStudents &&
          this.institution &&
          this.institution.show_kl_reports
        ) {
          options.push({
            text: this.$t("reusable.customFab.assessmentByStudent"),
            icon: "book_2",
            to: {
              name: "AssessmentStudentsView",
              params: { section_id: this.sectionId },
            },
          });
        }

        // Evaluaciones de la Sección (Profesor)
        if (
          shouldInclude("showEvaluatorEvaluations") &&
          this.showEvaluatorEvaluations &&
          this.sectionId
        ) {
          options.push({
            text: `${this.$getVisibleNames(
              "evaluations2.evaluation",
              true,
              "Evaluaciones"
            )} ${this.$t("reusable.customFab.of")} ${this.$getVisibleNames(
              "teaching.section",
              false,
              "Sección"
            )}`,
            icon: "patient_list",
            to: {
              name: "EvaluatorEvaluationsListView",
              params: { section_id: this.sectionId },
            },
          });
        }

        // Reporte de Evaluación
        if (
          shouldInclude("showAssessmentRangeSectionView") &&
          this.showAssessmentRangeSectionView &&
          this.institution &&
          this.institution.kl_assessment &&
          this.institution.show_kl_reports &&
          this.sectionId
        ) {
          options.push({
            text: this.$t("reusable.customFab.assessmentReport"),
            icon: "view_comfy",
            to: {
              name: "AssessmentRangeSectionView",
              params: { section_id: this.sectionId },
            },
          });
        }

        // Progresión de Evaluaciones
        if (
          shouldInclude("showProfessorProgressionReportView") &&
          this.showProfessorProgressionReportView &&
          this.institution &&
          this.institution.kl_assessment &&
          this.institution.show_kl_reports &&
          this.sectionId
        ) {
          options.push({
            text: `${this.$t(
              "reusable.customFab.progressionOf"
            )} ${this.$getVisibleNames(
              "evaluations2.evaluation",
              true,
              "Evaluaciones"
            )}`,
            icon: "dashboard_2",
            to: {
              name: "ProfessorProgressionReportView",
              params: { section_id: this.sectionId },
            },
          });
        }

        // Notas de la Sección
        if (
          shouldInclude("showNewFullGradingView") &&
          this.showNewFullGradingView &&
          this.sectionId
        ) {
          options.push({
            text: `${this.$t(
              "reusable.customFab.gradesOf"
            )} ${this.$getVisibleNames("teaching.section", false, "Sección")}`,
            icon: "table_chart",
            to: {
              name: "NewFullGradingView",
              params: { section_id: this.sectionId },
            },
          });
        }
      }
      // Planificación (solo si no es Duoc)
      if (
        shouldInclude("showPlannification") &&
        this.showPlannification &&
        this.institution &&
        this.institution.internal_use_id != "duoc.cl" &&
        (this.egressProfileMatter || this.matterId)
      ) {
        options.push({
          text: this.$getVisibleNames(
            "teaching.mattersessionplanning",
            false,
            "Planificación de la Asignatura"
          ),
          icon: "fact_check",
          to: {
            name: "SessionPlanningView",
            params: {
              egress_profile_matter_id:
                this.egressProfileMatter?.id || this.matterId,
              section_id: this.sectionId ? this.sectionId : 0,
            },
          },
        });
      }
      // Logros / Progresión de Competencias - solo estudiantes
      if (
        shouldInclude("showAchievements") &&
        this.showAchievements &&
        this.user &&
        this.user.is_student
      ) {
        options.push({
          text: `${this.$t(
            "reusable.customFab.progression"
          )} ${this.$getVisibleNames("mesh.competence", true, "Competencias")}`,
          icon: "award_star",
          to: {
            name: "AchievementsPerCompetenceView",
          },
        });
      }

      return options;
    },
    fetchEgressProfileFromMatter() {
      // Si ya tenemos el objeto en el store, extraer egress_profile directamente sin fetch
      if (this.egressProfileMatter) {
        this.egressProfileMatterId = this.egressProfileMatter.egress_profile;
        return;
      }
      // Evitar peticiones concurrentes
      if (!this.matterId || this.fetchingMatter) return;
      this.fetchingMatter = true;
      this.$store
        .dispatch(names.FETCH_EGRESS_PROFILE_MATTER, this.matterId)
        .then((response) => {
          if (response) {
            this.egressProfileMatterId = response.egress_profile;
          }
        })
        .finally(() => {
          this.fetchingMatter = false;
        });
    },
  },
  watch: {
    matterId: {
      immediate: true,
      handler() {
        this.fetchEgressProfileFromMatter();
      },
    },
    // Sin immediate: el watcher de matterId ya cubre la carga inicial.
    // Este reacciona solo si el objeto llega al store después (cargado por otro componente).
    egressProfileMatter(val) {
      if (val) {
        this.egressProfileMatterId = val.egress_profile;
      }
    },
  },
};
</script>

<style scoped>
/* Fijo bajo el navbar al hacer scroll. El top real se mide en JS desde
#nav-bar (updateStickyTop) porque en responsive el navbar tiene height:auto;
estos valores son solo respaldo hasta el primer cálculo (~65.6px del navbar
de escritorio, 98px del móvil con riel, con 2px de solape). */
.breadcrumb-sticky {
  position: sticky;
  top: 64px;
  padding-top: 2px;
  z-index: 800; /* sobre el contenido, bajo navbar (1000+) y loadingBar (900) */
  background-color: var(--kl-surface, #ffffff);
  /* Misma sombra que .navbar (NavBar.vue): cuando hay breadcrumb, el navbar
  la oculta (body.kl-has-breadcrumb) y es este borde inferior el que separa
  el bloque fijo del contenido. */
  box-shadow: 0 1px 4px rgba(16, 38, 73, 0.08);
}

@media (max-width: 768px) {
  .breadcrumb-sticky {
    top: 96px;
  }
}

@media print {
  .breadcrumb-sticky {
    position: static;
    box-shadow: none;
  }
}

.breadcrumb {
  background: none;
}

.breadcrumb-item.active {
  color: #6c757d;
  cursor: default;
  margin: 0;
  padding: 0;
  display: inline-flex;
  align-items: center;
}

.breadcrumb-item a {
  text-decoration: none;
  cursor: pointer;
}

.breadcrumb-item:not(.active) {
  cursor: pointer;
  margin: 0;
  padding: 0;
}

.breadcrumb-item a:hover {
  text-decoration: underline;
}

.breadcrumb > li + li:before {
  color: #006ec8;
  content: "❯ ";
  padding: 0;
  padding-inline: 0.65em;
  margin: 0;
}

/* Estilos para el dropdown en el breadcrumb */
.breadcrumb-dropdown {
  display: inline-block !important;
}

.breadcrumb-dropdown >>> .btn {
  padding: 0 !important;
  margin: 0 !important;
  border: none !important;
  background: transparent !important;
  color: #6c757d !important;
  font-weight: 400 !important;
  box-shadow: none !important;
  display: inline-flex !important;
  align-items: center !important;
  text-decoration: none !important;
  vertical-align: baseline !important;
  line-height: inherit !important;
}

.breadcrumb-dropdown >>> .btn:hover {
  color: #495057 !important;
  text-decoration: none !important;
  background: transparent !important;
  cursor: pointer;
}

.breadcrumb-dropdown >>> .btn:focus,
.breadcrumb-dropdown >>> .btn:active,
.breadcrumb-dropdown >>> .btn.show {
  box-shadow: none !important;
  background: transparent !important;
  color: #6c757d !important;
}

.dropdown-text {
  color: #6c757d;
  display: inline;
  vertical-align: baseline;
}

.chevron-icon {
  color: #6c757d;
  font-size: 0.75em;
  vertical-align: baseline;
}

.breadcrumb-dropdown >>> .dropdown-menu {
  font-size: 0.9rem;
  margin-top: 0.25rem;
}

.breadcrumb-dropdown >>> .dropdown-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
}

.breadcrumb-dropdown >>> .dropdown-item:hover {
  background-color: #ebebeb;
}

.dropdown-icon {
  display: inline-flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.6);
  font-size: 1rem;
}

.dropdown-icon-img {
  width: 1.2rem;
  height: 1.2rem;
  filter: brightness(0) opacity(0.6);
}
</style>
